/**
 * EarningsList Component
 *
 * List of earnings with filtering.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import type { Earning } from '../hooks';

interface EarningsListProps {
  earnings: Earning[];
  isLoading: boolean;
}

const STATUS_CONFIG = {
  pending: { label: 'Pending', bg: 'bg-amber-100', color: 'text-amber-700' },
  available: { label: 'Available', bg: 'bg-green-100', color: 'text-green-700' },
  paid_out: { label: 'Paid Out', bg: 'bg-blue-100', color: 'text-blue-700' },
};

export const EarningsList: React.FC<EarningsListProps> = ({
  earnings,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
            <div className="flex gap-4">
              <div className="w-16 h-16 bg-gray-200 rounded-lg" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-200 rounded w-1/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (earnings.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl">
        <MoneyIcon className="w-12 h-12 mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No earnings yet</h3>
        <p className="text-gray-500">
          Your earnings from bookings will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {earnings.map((earning) => {
        const status = STATUS_CONFIG[earning.status];
        const nights = Math.ceil(
          (earning.checkOut.getTime() - earning.checkIn.getTime()) / (1000 * 60 * 60 * 24)
        );

        return (
          <div
            key={earning.id}
            className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Listing Image */}
              <img
                src={earning.listingImage}
                alt={earning.listingTitle}
                className="w-full sm:w-20 h-32 sm:h-20 rounded-lg object-cover"
              />

              {/* Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <h3 className="font-medium text-gray-900 truncate">
                      {earning.listingTitle}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {earning.checkIn.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                      {' - '}
                      {earning.checkOut.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                      {' · '}
                      {nights} {nights === 1 ? 'night' : 'nights'}
                    </p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                    {status.label}
                  </span>
                </div>

                {/* Guest Info */}
                <div className="flex items-center gap-2 mb-3">
                  <img
                    src={earning.guestAvatar}
                    alt={earning.guestName}
                    className="w-6 h-6 rounded-full object-cover"
                  />
                  <span className="text-sm text-gray-600">{earning.guestName}</span>
                </div>

                {/* Amounts */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                  <div>
                    <span className="text-gray-500">Gross: </span>
                    <span className="text-gray-900">${earning.grossAmount.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Commission: </span>
                    <span className="text-red-600">-${earning.platformCommission.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-gray-700 font-medium">Net: </span>
                    <span className="text-green-600 font-semibold">
                      ${earning.netEarnings.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Availability Info */}
                {earning.status === 'pending' && earning.availableAt && (
                  <p className="text-xs text-amber-600 mt-2">
                    Available after {earning.availableAt.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                )}
              </div>

              {/* Link */}
              <div className="flex sm:flex-col sm:justify-center">
                <Link
                  to={`/host/bookings/${earning.bookingId}`}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  View Booking →
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Icons
const MoneyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export default EarningsList;
